import {
  capitalize,
  compactInteger,
  formatNumber,
  intComma,
  pluralize,
  toFixed,
} from 'humanize-plus'
import moment, { Moment, MomentInput } from 'moment'
import spacetime, { Spacetime } from 'spacetime'

import { ReportDateHistogramType } from '@driftt/api-types'
import { formatDate } from '@driftt/dds.reports'

import { EN_DASH } from 'constants/characters'

export const spacetimeToMoment = (s: Spacetime) => moment(s.toNativeDate())
export const momentToSpacetime = (m: Moment) => spacetime(m.toDate())

const formatCurrencyUnrounded = (value) => {
  const decimalPlaces = value === 0 ? 0 : 2
  return formatNumber(value, decimalPlaces)
}

export const parseDateToRange = (
  startDate: Moment,
  endDate: Moment
): string => {
  if (startDate.isSame(endDate)) {
    return startDate.format('MMMM D, YYYY')
  }

  const startDateFormat = `MMMM D${
    startDate.isSame(endDate, 'year') ? '' : ', YYYY'
  }`
  const endDateFormat = startDate.isSame(endDate, 'month')
    ? 'D, YYYY'
    : 'MMMM D, YYYY'
  return `${startDate.format(startDateFormat)} – ${endDate.format(
    endDateFormat
  )}`
}

export const renderDateRange = (
  date: string,
  aggregateType: ReportDateHistogramType,
  fullRange: { startDate: string; endDate: string }
) => {
  const isToday = moment().diff(moment(date), 'days') === 0

  const beginDate = moment(date)
  let end = moment(date)

  switch (aggregateType) {
    case ReportDateHistogramType.MONTH:
      end = moment(date).endOf('month')
      break
    case ReportDateHistogramType.WEEK:
      end = moment(date).add(7, 'days')
      break
    case ReportDateHistogramType.DAY:
    default:
      end = moment(date)
  }

  const rangeStart = moment(fullRange.startDate)
  const rangeEnd = moment(fullRange.endDate)

  const endDate = end.diff(rangeEnd, 'days') >= 0 ? rangeEnd : end

  const isPartial =
    ReportDateHistogramType.DAY !== aggregateType &&
    (beginDate.diff(rangeStart, 'days') < 0 ||
      rangeEnd.diff(endDate, 'days') < 0)

  const dateString =
    beginDate.diff(endDate, 'days') === 0
      ? TypeFormats.DATE_SLASH(beginDate)
      : `${TypeFormats.DATE_SLASH(
          beginDate.toString()
        )} ${EN_DASH} ${TypeFormats.DATE_SLASH(endDate.toString())}`

  return {
    dateString,
    qualifer:
      isPartial || isToday ? `*Partial ${aggregateType.toLowerCase()}` : null,
  }
}

export const mapDatesToSeriesLabels = (
  startDate,
  endDate,
  prevStart,
  prevEnd
) => {
  return {
    series: parseDateToRange(startDate, endDate),
    previousSeries: parseDateToRange(prevStart, prevEnd),
  }
}

/* eslint-disable @typescript-eslint/explicit-member-accessibility */
export class TypeFormats {
  static RAW_HUMANIZED = (value: number) => formatNumber(value)
  static FIXED = (value: number) => compactInteger(value, 1)
  static ROUND = (value: number) => compactInteger(value)
  static DATETIME = (value: MomentInput) => moment(value).format('M/D')
  static DATE = (value: MomentInput) => moment(value).format('YYYY-MM-DD')
  static DATE_SLASH = (value: MomentInput) => moment(value).format('MM/DD/YYYY')
  static HUMAN_READABLE_SIMPLE_DATE = (date: Date | string | number) =>
    formatDate(date, 'MMMM do, yyyy')
  static HUMAN_READABLE_FULL_DATE = (date: Date | string | number) =>
    formatDate(date, 'eeee, MMMM do, yyyy')

  static CURRENCY = (value: number) => `$${compactInteger(value, 1)}`
  static CURRENCY_UNROUNDED = (value: number) =>
    `$${formatCurrencyUnrounded(value)}`
  static PERCENT = (value: number) => `${intComma(Math.round(value)) || 0}%`
  static PERCENT_WITH_DECIMAL = (value: number) =>
    `${value ? intComma(value, 1) : 0}%`
  static SAFE_PERCENT_WITH_DECIMAL = (value: number) =>
    `${isFinite(value) ? intComma(value, 1) : value > 0 ? 100 : 0}%`
  static ONE_DECIMAL = (value: number) => toFixed(value, 1)
  static CONVERSIONRATE = (value: number) =>
    `${formatNumber(isFinite(value) ? value : 0, 1)}%`
  static CAPITALIZE = (value: string) => capitalize(value)
  static TIME = (value: MomentInput, timezone: string) =>
    moment(value).tz(timezone).format('hh:mm A z')
}
/* eslint-enable @typescript-eslint/explicit-member-accessibility */
export const getHumanizedDuration = (duration, unitOfTime = 'milliseconds') => {
  const momentDuration = moment.duration(
    duration,
    unitOfTime as moment.unitOfTime.DurationConstructor
  )
  const days = momentDuration.days()
  const hours = momentDuration.hours()
  const minutes = momentDuration.minutes()
  const seconds = momentDuration.seconds()
  if (days) {
    return `${days}d ${hours}h ${minutes}m ${seconds}s`
  }
  if (hours) {
    return `${hours}h ${minutes}m ${seconds}s`
  }
  if (minutes) {
    return `${minutes}m ${seconds}s`
  }
  return seconds ? `${seconds}s` : `0s`
}

export const getHumanizedDurationMilliseconds = (duration) => {
  const momentDuration = moment.duration(duration, 'milliseconds')
  const months = momentDuration.months()
  const days = momentDuration.days()
  const hours = momentDuration.hours()
  const minutes = momentDuration.minutes()
  const seconds = momentDuration.seconds()
  if (months) {
    return `${months}m ${days}d ${hours}h ${minutes}m ${seconds}s`
  }
  if (days) {
    return `${days}d ${hours}h ${minutes}m ${seconds}s`
  }
  if (hours) {
    return `${hours}h ${minutes}m ${seconds}s`
  }
  if (minutes) {
    return `${minutes}m ${seconds}s`
  }
  return seconds ? `${seconds}s` : `0s`
}

export const getHumanizedShortDuration = (duration, noSeconds = false) => {
  const momentDuration = moment.duration(duration)
  const days = momentDuration.days()
  const hours = momentDuration.hours()
  const minutes = momentDuration.minutes()
  const seconds = momentDuration.seconds()

  if (days) {
    return hours ? `${days}d ${hours}h` : `${days}d`
  }
  if (hours) {
    return minutes ? `${hours}h ${minutes}m` : `${hours}h`
  }
  if (minutes) {
    if (noSeconds) {
      if (seconds > 30) {
        return `${minutes + 1}m`
      }
      return `${minutes}m`
    }
    return seconds ? `${minutes}m ${seconds}s` : `${minutes}m`
  }
  return seconds ? `${seconds}s` : `0s`
}

export const getUnixTimestamp = (date) => moment.utc(date).valueOf()

export const getStartOfDayUnix = (date: MomentInput) =>
  moment.utc(date).startOf('day').valueOf()

export const militaryToStandard = (value) => {
  switch (value) {
    case 0:
      return '12am'
    case 12:
      return '12pm'
    default:
      return value < 12 ? `${value}am` : `${value - 12}pm`
  }
}

export const getTimeSinceX = (start, end) => {
  const daysDifferent = moment(end).diff(start, 'days')

  if (daysDifferent === 7) return 'previous week'

  if (daysDifferent >= 28 && daysDifferent <= 31) return 'previous month'

  return 'previous time period'
}

export const wereOrWas = (previousValue?: number) =>
  !!previousValue && previousValue === 1 ? 'was' : 'were'

export const visitorOrVisitors = (previousValue?: number) =>
  !!previousValue && previousValue === 1
    ? pluralize(previousValue, 'visitor')
    : 'visitors'

export const buildValue = (currentValue = 0, previousValue = 0) => {
  if (currentValue === 0 && previousValue === 0) {
    return EN_DASH
  }
  if (previousValue !== 0 && currentValue === 0) {
    return TypeFormats.PERCENT_WITH_DECIMAL(0)
  }
  const conversionRate =
    previousValue !== 0 ? (currentValue / previousValue) * 100 : 0
  return conversionRate < 1
    ? TypeFormats.CONVERSIONRATE((currentValue / previousValue) * 100)
    : TypeFormats.PERCENT_WITH_DECIMAL((currentValue / previousValue) * 100)
}

export const toSnakeCase = (str: string) =>
  str.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`)

export const calcDelta = (
  currentY: number,
  previousY: number,
  invert: boolean
) => {
  if (previousY !== 0) {
    return ((currentY - previousY) / previousY) * 100 * (invert ? -1 : 1)
  }

  if (currentY !== 0) {
    return ((previousY - currentY) / currentY) * 100 * (invert ? 1 : -1)
  }

  return 0
}
